import React, { useEffect, useState, useRef, useCallback } from 'react'
import {Link} from 'react-router-dom'
import TeamRankBlock from './components/TeamRankBlock'
import { useLocation } from 'react-router-dom'
import { db } from '../..'
import { ref, onValue, get, set, push } from 'firebase/database'
import { useNavigate } from 'react-router-dom'
import Loading from '../components/Loading'
import BottomMenu from './components/BottomMenu'

const GameHome = ({userData}) => {
    const location = useLocation();
    const { gameId, teamId } = location.state;
    const navigate = useNavigate();

    // ゲームデータ
    const [gameData, setGameData] = useState({info: {}, teams: [], status: {}});
    const [teamData, setTeamData] = useState({});
    const roles = ["逃走", "鬼"]
    const roleSub = [null, null, "鬼", "逃走"]
    const [remainingTime, setRemainingTime] = useState(null);
    const intervalRef = useRef(null);
    // const [timeRate, setTimeRate] = useState(null);
    const [timeStatus, setTimeStatus] = useState(null); // ゲームの状態
    const statusText = ["開始前", "自動", "逃走チーム準備", "ゲーム進行中", "中断", "終了"];
    const [missionActive, setMissionActive] = useState(false);
    const [specialStatus, setSpecialStatus] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const userDataRef = useRef(userData); // マウント時のみ実行されるeffect内から常に最新のuserDataを参照するためのref

    useEffect(() => {
        userDataRef.current = userData;
    }, [userData]);

    // ゲーム参加履歴を記録
    // 参加履歴の記録はあくまでサブ機能のため、必要なデータが揃っていない場合や
    // 通信エラー等で書き込みに失敗した場合でも、ゲーム画面の表示・プレイには影響させない
    // （記録が欠けるだけで、他の処理はそのまま継続する）
    const recordJoinedHistory = useCallback((gameId, gameDataTmp) => {
        const currentUserData = userDataRef.current;
        if(currentUserData.history && currentUserData.history.join) {
            if(Object.keys(currentUserData.history.join).indexOf(gameId) !== -1) {
                return;
            }
        }

        // チーム編集タイミングのズレ等でteamIdに対応するチームが存在しない場合があるため、
        // 存在チェックを行ってから書き込む（存在しなければ記録をスキップするのみで処理は継続）
        const team = gameDataTmp && gameDataTmp.teams ? gameDataTmp.teams[teamId] : undefined;
        if(!currentUserData.uid || !gameDataTmp || !gameDataTmp.info || !team) {
            console.error('参加履歴の記録に必要なデータが揃っていないため、記録をスキップしました。', {gameId, teamId, gameDataTmp});
            return;
        }

        const userRef = ref(db, 'users/' + currentUserData.uid + '/history/join/' + gameId);
        set(userRef, {
            date: new Date().toISOString(),
            name: gameDataTmp.info.name,
            team: {
                name: team.name,
                color: team.color
            }
        }).catch((error) => {
            // 通信環境等アプリ側で解決できない原因で失敗する場合があるため、
            // ユーザーへの通知はせずログのみ残し、ゲームのプレイは継続させる
            console.error('参加履歴の記録に失敗しました:', error);
        });
    }, [teamId]);

    useEffect(() => {
        if(!gameId) {
            alert("ゲームIDを取得できませんでした。");
            navigate('/');
        };
        if(!teamId && teamId !== 0) {
            alert("所属チームが設定されていません。");
            navigate('/');
        };

        let unsubscribeGameLog = null;

        // ゲームデータ取得
        const gameRef = ref(db, 'games/' + gameId);
        get(gameRef).then(snapshot => {
            setGameData({...snapshot.val(), status:{}});

            // ゲームに参加した記録をユーザーデータに追加
            recordJoinedHistory(gameId, snapshot.val());

            // ゲームログデータ取得
            const gameLogRef = ref(db, 'gameLogs/' + gameId);
            unsubscribeGameLog = onValue(gameLogRef, (snapshot2) => {
                // アクティベート取消しでgameLogsノード自体が削除された場合
                if(!snapshot2.exists()) {
                    alert("ゲームのアクティベートが取り消されました。");
                    navigate('/');
                    return;
                }

                // 一時的なデータ不整合（配信タイミングのズレ等）で描画がクラッシュし
                // 画面が真っ白になることがないよう、teams/statusは安全な既定値にフォールバックする
                const gameLogVal = snapshot2.val() || {};
                const teamsData = Array.isArray(gameLogVal.teams) ? gameLogVal.teams : [];

                //チーム情報などをGameDataへ
                setGameData((prev) => ({
                    ...snapshot.val(),
                    missions: gameLogVal.missions,
                    teams: teamsData.map((team, index) => ({
                        ...team,
                        id: index
                    })),
                    history: gameLogVal.history,
                    status: gameLogVal.status || {}
                }));
                setTeamData(teamsData[Number(teamId)] || {});
                checkMission(gameLogVal.missions);
            });
        }).catch((error) => {
            console.error('ゲームデータの取得に失敗しました:', error);
            setLoadError(true);
            alert("ゲームデータの取得に失敗しました。もう一度お試しください。");
        });

        return () => {
            if(unsubscribeGameLog) unsubscribeGameLog();
        };
    }, [gameId, teamId, navigate, recordJoinedHistory]);

    // calculateTime/checkSpecialStatusはintervalRefやteamData等を相互参照する密結合なタイマー処理のため、
    // useCallback化して依存配列に含めるとタイマーの再生成ロジックに影響する恐れがある。
    // このeffectはgameDataの変化時にのみ発火させる意図的な設計として除外する。
    useEffect(() => {
        if(gameData.status) {
            calculateTime();
            checkSpecialStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameData]);

    // 残り時間計算
    const calculateTime = () => {
        // インターバルクリア
        if(intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if(gameData.status.type) {
            // ゲーム開始前
            if(gameData.status.type === 0) {
                setTimeStatus(0);
                setRemainingTime('00:00:00');
            }

            // ゲーム進行中（オート実行）
            if(gameData.status.type === 1) {
                setTimeStatus(1);
                
                if(new Date(gameData.info.startTime).getTime() > new Date().getTime()) {
                    //開始前
                    const startTime = new Date(gameData.info.startTime);
                    setTimeStatus(0);
                    recalculateTime(startTime);
                    intervalRef.current = setInterval(() => {
                        recalculateTime(startTime);
                    }, 1000);
                } else if(new Date(gameData.info.prepareLimitTime).getTime() > new Date().getTime()) {
                    //逃走チーム準備
                    const escapeTime = new Date(gameData.info.prepareLimitTime);
                    setTimeStatus(2);
                    recalculateTime(escapeTime);
                    intervalRef.current = setInterval(() => {
                        recalculateTime(escapeTime);
                    }, 1000);
                } else if(new Date(gameData.info.endTime).getTime() > new Date().getTime()) {
                    //ゲーム進行中
                    const endTime = new Date(gameData.info.endTime);
                    setTimeStatus(3);
                    recalculateTime(endTime);
                    intervalRef.current = setInterval(() => {
                        recalculateTime(endTime);
                    }, 1000);
                } else {
                    //終了
                    setTimeStatus(5);
                    setRemainingTime('00:00:00');
                }
            }

            // 逃走準備（手動実行）
            if(gameData.status.type === 2) {
                setTimeStatus(2);
            }

            // ゲーム進行中（手動実行）
            if(gameData.status.type === 3) {
                setTimeStatus(3);
            }

            // ゲーム中断中（手動実行 / 自動実行）
            if(gameData.status.type === 4) {
                setTimeStatus(4);
            }

            // ゲーム終了
            if(gameData.status.type === 5) {
                setTimeStatus(5);
            }
        }
    }

    // 残り時間再計算
    const recalculateTime = (limitTime) => {
        const nowTime = new Date();
        const limit = new Date(limitTime);
        const diffTime = limit - nowTime;

        if(diffTime < 0 && intervalRef.current !== null) {calculateTime(); return;}

        const diffHour = Math.floor(diffTime / (1000 * 60 * 60));
        const diffMin = Math.floor((diffTime - diffHour * 1000 * 60 * 60) / (1000 * 60));
        const diffSec = Math.floor((diffTime - diffHour * 1000 * 60 * 60 - diffMin * 1000 * 60) / 1000);
        setRemainingTime(`${String(diffHour).padStart(2, '0')}:${String(diffMin).padStart(2, '0')}:${String(diffSec).padStart(2, '0')}`);

        checkSpecialStatus();
    }

    // 役を交代してから5分以内の場合、無敵or待機状態（特別状態）をセット
    const checkSpecialStatus = () => {
        if(!teamData.timeRoleChanged) return;

        const date = new Date();
        const lastChangeRole = new Date(teamData.timeRoleChanged);
        const diffTime = date.getTime() - lastChangeRole.getTime();

        if(diffTime < 300000 && gameData.info.gameMode !== 1) {
            if(teamData.role === 0) {
                setSpecialStatus("5分間無敵");
            } else {
                setSpecialStatus("その場で5分待機");
            }
        } else {
            setSpecialStatus(null);
        }
    }


    // 受付中のミッションの有無を確認
    const checkMission = (missions) => {
        if(missions) {
            let missionFlag = false;
            const date = new Date();

            Object.keys(missions).forEach(key => {
                const mission = missions[key];
                if(date.getTime() < (new Date(mission.deadline)).getTime()) {
                    missionFlag = true;
                }
            });

            setMissionActive(missionFlag);
        } else {
            setMissionActive(false)
        }
    }

    // リタイア
    const retire = () => {
        if(window.confirm("リタイアしますか？")) {
            const gameLogRef = ref(db, 'gameLogs/' + gameId + '/teams/' + teamId);

            //リタイア処理
            set(gameLogRef, {
                ...teamData,
                retired: true
            })
            .then(() => {
                //ログに記録
                const historyRef = ref(db, 'gameLogs/' + gameId + '/history');
                const date = new Date();
                push(historyRef, {
                    time: date.toISOString(),
                    actionNum: 6,
                    action: "リタイア",
                    teamId: teamId,
                    team: teamData
                })
                .then(() => {
                    alert("リタイアしました。");
                })
                .catch((error) => {
                    console.error('リタイア履歴の記録に失敗しました:', error);
                    alert("リタイア履歴の記録に失敗しました。");
                });
            })
            .catch((error) => {
                console.error('リタイア処理に失敗しました:', error);
                alert("リタイア処理に失敗しました。もう一度お試しください。");
            });
        }
    }

    // ゲームに復帰
    const returnGame = () => {
        if(window.confirm("リタイアを取消し、ゲームに復帰しますか？")) {
            const gameLogRef = ref(db, 'gameLogs/' + gameId + '/teams/' + teamId);

            //リタイア処理
            set(gameLogRef, {
                ...teamData,
                retired: false
            })
            .then(() => {
                //ログに記録
                const historyRef = ref(db, 'gameLogs/' + gameId + '/history');
                const date = new Date();
                push(historyRef, {
                    time: date.toISOString(),
                    actionNum: 8,
                    action: "リタイア取消し",
                    teamId: teamId,
                    team: teamData
                })
                .then(() => {
                    alert("ゲームに復帰しました。");
                })
                .catch((error) => {
                    console.error('復帰履歴の記録に失敗しました:', error);
                    alert("復帰履歴の記録に失敗しました。");
                });
            })
            .catch((error) => {
                console.error('復帰処理に失敗しました:', error);
                alert("復帰処理に失敗しました。もう一度お試しください。");
            });
        }
    }

  return (
    <div className='lg:grid lg:grid-cols-2 max-w-6xl mx-auto my-6'>
        <Loading show={!loadError && gameData.info.name === undefined} text="ゲームデータを読込み中…" />

        {loadError && (
            <div className='text-center text-red-600 my-6'>
                <i className='bi bi-exclamation-triangle-fill me-2'></i>ゲームデータの取得に失敗しました。再読み込みしてください。
            </div>
        )}

        {/* チーム情報表示 */}
        <div className='rounded-xl bg-slate-50 p-4 text-center m-2 shadow-sm'>
            <div className='text-lime-600 mb-1'>{gameData.info.name}</div>
            <div className='text-xl tracking-widest mb-3'><i className='bi bi-people-fill text-purple-400 me-1'></i>{teamData.name}</div>

            {/* ポイント表示 */}
            <div className='text-xl tracking-widest text-gray-700'>
                <i className='bi bi-coin text-yellow-500 me-1'></i>
                <span className='font-bold text-lime-600'>{teamData.score}</span> pt
            </div>
        </div>

        <div className='grid grid-cols-2 mb-4'>
            {/* チームステータス表示 */}
            <div className='rounded-xl bg-slate-50 p-4 text-center m-2 shadow-sm'>
                <div className='text-gray-600 mb-1'>チームの状態</div>
                <div className='text-2xl tracking-widest mb-2 text-blue-600'>{roles[teamData.role]}<div className='text-sm text-gray-600'>{roleSub[teamData.role]}</div></div>

                {/* 特別ステータス（無敵・待機） */}
                {
                    specialStatus && (
                        <div className='text-base mb-1 text-red-600 tracking-wider'>{specialStatus}</div>
                    )
                }

                {
                    teamData.retired ? (
                        <div className='text-red-600 p-1 bg-red-50 rounded-md'>リタイア中</div>
                    ) : (
                        <Link to={timeStatus === 3 ? "/game/catch" : null} state={{gameId: gameId, teamId: teamId, gameMode: gameData.info.gameMode ? gameData.info.gameMode : 0}} onClick={() => {if(timeStatus !== 3) {alert("この操作ができるのは、ゲーム進行中のときのみです。")}}} >
                            <button className='bg-lime-300 rounded-lg text-black p-2 mt-3 w-full shadow-sm border border-lime-400 hover:bg-lime-400'>
                                {teamData.role === 0 ? "つかまった" : "つかまえた"}
                            </button>
                        </Link>
                    )
                }
                
            </div>

            {/* 時間表示 */}
            <div className='rounded-xl bg-slate-50 p-4 text-center m-2 shadow-sm'>
                <div className='text-lg mb-3 text-gray-600'>ゲームの状態</div>
                <div className='text-purple-600 text-xl mb-1'>{statusText[Number(timeStatus)]}</div>
                <div className='text-2xl tracking-widest mb-2 text-gray-800 font-semibold'>{remainingTime}</div>

                {/* <div className='text-base mb-3 text-gray-600'>{timeRate}</div> */}
            </div>
        </div>

        {/* ミッション通知表示 */}
        {
            missionActive ? (
                <Link to="/game/missions" state={{gameId: gameId, teamId: teamId}} className='block bg-purple-500 p-4 m-2 mb-4 rounded-lg cursor-pointer shadow-sm'>
                    <h3 className='text-2xl text-purple-50 font-bold mb-1'>ミッション発令中！</h3>
                    <p className='text-base text-lime-300 font-bold text-right'>詳細はこちら<i className="bi bi-chevron-double-right mx-2"></i></p>
                </Link>
            ) : (
                <Link to="/game/missions" state={{gameId: gameId, teamId: teamId}} className='block relative bg-slate-50 p-4 m-2 mb-4 rounded-lg cursor-pointer shadow-sm hover:bg-slate-100'>
                    <div className='p-2'>
                        <i className="align-middle me-4 bi bi-clipboard-check text-3xl text-purple-300"></i>
                        <h2 className='align-middle inline text-xl text-gray-600 mb-1 tracking-widest'>ミッション</h2>
                    </div>
                    <div className='absolute inline-block end-2 top-6 text-gray-300'>
                        <i className="bi bi-chevron-right mx-2 text-2xl"></i>
                    </div>
                </Link>
            )
        }

        <div className='grid grid-cols-2'>
            {/* 地図のページ */}
            <Link to="/game/map" state={{gameId: gameId, teamId: teamId, center: {lat: gameData.info.mapCenter?.lat, lng: gameData.info.mapCenter?.lng, zoom: gameData.info.mapCenter?.zoom}}} className='relative block rounded-xl bg-slate-50 p-4 m-2 mb-4 flex hover:bg-slate-100 shadow-sm'>
                <div className='p-2'>
                    <i className="align-middle me-4 bi bi-map-fill text-3xl text-purple-300"></i>
                    <h2 className='align-middle inline text-xl text-gray-600 mb-1 tracking-widest'>マップ</h2>
                </div>
                <div className='absolute inline-block end-2 top-6 text-gray-300'>
                    <i className="bi bi-chevron-right mx-2 text-2xl"></i>
                </div>
            </Link>

            {/* 履歴ページ */}
            <Link to="/game/history" state={{gameId: gameId, teamId: teamId}} className='relative block rounded-xl bg-slate-50 p-4 m-2 mb-4 flex hover:bg-slate-100 shadow-sm'>
                <div className='p-2'>
                    <i className="align-middle me-4 bi bi-clock-history text-3xl text-purple-300"></i>
                    <h2 className='align-middle inline text-xl text-gray-600 mb-1 tracking-widest'>履歴</h2>
                </div>
                <div className='absolute inline-block end-2 top-6 text-gray-300'>
                    <i className="bi bi-chevron-right mx-2 text-2xl"></i>
                </div>
            </Link>
        </div>
        

        {/* チーム情報 */}
        <div className='my-4 mb-6'>
            <h2 className='text-center text-lg text-gray-600'><i className="bi bi-trophy mx-2 text-purple-600"></i>ランキング</h2>

            <div className='grid grid-cols-3 mb-2'>
                {
                    gameData.teams.sort((a, b) => b.score - a.score).filter((team, index) => index < 3).map((team, index) => (
                        <TeamRankBlock key={index} name={team.name} rank={index + 1} score={team.score} onClick={() => {}} />
                    ))
                }
            </div>
            
            <Link to="/game/allTeam" state={{gameId: gameId, teamId: teamId}} className='block rounded-lg mx-2 text-center p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 shadow-sm'>すべてのチーム <i className="bi bi-chevron-double-right mx-1"></i></Link>
        </div>

        <div className='my-4 mb-6'>

            {/* リタイアなど */}
            <div className='grid grid-cols-3 gap-3 mx-2'>
                {
                    teamData.retired ? (
                        <button className='block rounded-xl bg-slate-50 p-4 text-center hover:bg-slate-100 shadow-sm' onClick={() => returnGame()}>
                            <i className='bi bi-arrow-clockwise me-2 text-2xl mb-2 text-green-600'></i>
                            <h2 className='text-green-700 text-base'>ゲームに復帰</h2>
                        </button>
                    ) : (
                        <button className='block rounded-xl bg-slate-50 p-4 text-center hover:bg-slate-100 shadow-sm' onClick={() => retire()}>
                            <i className='bi bi-x-lg me-2 text-2xl mb-2 text-red-600'></i>
                            <h2 className='text-red-700 text-base'>リタイア</h2>
                        </button>
                    )
                }
                

                <button onClick={() => {window.location.reload();}} className='block rounded-xl bg-slate-50 p-4 text-center hover:bg-slate-100 shadow-sm'>
                    <i className='bi bi-arrow-clockwise me-2 text-2xl mb-2 text-gray-600'></i>
                    <h2 className='text-gray-700 text-base'>再読み込み</h2>
                </button>

                <Link to="specialItems" state={{gameId: gameId, teamId: teamId}} className='block rounded-xl bg-slate-50 p-4 text-center hover:bg-slate-100 shadow-sm'>
                    <i className='bi bi-collection me-2 text-2xl mb-2 text-gray-600'></i>
                    <h2 className='text-gray-700 text-base'>アイテム</h2>
                </Link>
            </div>
        </div>

        {/* 下部固定メニュー */}
        <BottomMenu gameId={gameId} teamId={teamId} />
    </div>
  )
}

export default GameHome